import type { EChartsOption } from '../echarts-types.ts';
import type { ChartConfig } from '../types.ts';
import { chartTitle, type OptionContext } from './shared.ts';

type SunburstConfig = Extract<ChartConfig, { type: 'sunburst' }>;
type SunburstNode = SunburstConfig['nodes'][number];
type SunburstChild = NonNullable<SunburstNode['children']>[number];

function resolveStanceColor(
  node: { color?: string; key?: string },
  praiseColor: string,
  frictionColor: string,
  fallback: string
): string {
  if (node.color === 'positive' || node.key === 'praise') return praiseColor;
  if (node.color === 'negative' || node.key === 'friction')
    return frictionColor;
  if (typeof node.color === 'string' && node.color.startsWith('#'))
    return node.color;
  return fallback;
}

function mapEmotion(
  node: SunburstChild,
  leafColor: string,
  labelColor: string
): Record<string, unknown> {
  return {
    name: node.name,
    value: typeof node.value === 'number' ? node.value : 0,
    itemStyle: { color: leafColor },
    // Inside labels sit on the wedge fill.
    label: { color: labelColor },
  };
}

function mapRoot(
  node: SunburstNode,
  praiseColor: string,
  frictionColor: string,
  index: number,
  praiseLabelColor: string,
  frictionLabelColor: string
): Record<string, unknown> {
  const fallback = index === 0 ? praiseColor : frictionColor;
  // Flat per stance: muted orange praise / dusty-rose friction.
  const color = resolveStanceColor(node, praiseColor, frictionColor, fallback);
  const isPraise =
    node.key === 'praise' || node.color === 'positive' || color === praiseColor;
  const labelColor = isPraise ? praiseLabelColor : frictionLabelColor;
  const children = (node.children ?? []).map((child) =>
    mapEmotion(child, color, labelColor)
  );

  return {
    name: node.name,
    value: node.value,
    itemStyle: { color },
    label: { color: labelColor },
    ...(children.length > 0 ? { children } : {}),
  };
}

export function buildSunburstOption(
  config: SunburstConfig,
  { theme, base }: OptionContext
): EChartsOption {
  const { textColor, dark } = theme;
  const borderColor = dark
    ? 'rgba(15, 15, 20, 0.92)'
    : 'rgba(255, 255, 255, 0.95)';
  // Warm praise (muted orange) + deeper dusty-rose friction — softer than brick,
  // still darker than praise so stance splits when hue is close.
  const praiseColor = dark ? '#c47a3a' : '#b86f32';
  const frictionColor = dark ? '#8b4554' : '#7a3d4a';
  // Mid-dark fills — light ink passes contrast on both.
  const onWedgeLabelColor = 'rgba(255, 255, 255, 0.95)';
  const data = config.nodes.map((node, i) =>
    mapRoot(
      node,
      praiseColor,
      frictionColor,
      i,
      onWedgeLabelColor,
      onWedgeLabelColor
    )
  );
  const outerCount = config.nodes.reduce(
    (n, node) => n + (node.children?.length ?? 0),
    0
  );
  const manyOuter = outerCount > 8;
  const outerR = manyOuter ? '75%' : '85%';

  const series = {
    type: 'sunburst' as const,
    // Wide outer band — emotion names live inside the wedges (no outside halo).
    radius: [0, outerR] as [number, string],
    center: ['50%', '50%'] as [string, string],
    sort: undefined,
    nodeClick: false as const,
    animation: false,
    // No hover restyle — thin/crowded wedges otherwise thrash redraws.
    emphasis: {
      disabled: true,
    },
    label: {
      rotate: 'radial' as const,
      color: textColor,
    },
    itemStyle: {
      borderColor,
      borderWidth: 2,
    },
    levels: [
      {},
      {
        // +/- stance cores
        r0: '0%',
        r: '22%',
        label: {
          rotate: 0,
          fontSize: 18,
          minAngle: 8,
        },
        itemStyle: {
          borderWidth: 3,
          borderColor,
        },
      },
      {
        // Emotion leaves — thick band, names inside.
        r0: '25%',
        r: outerR,
        itemStyle: {
          borderWidth: 2,
          borderColor,
        },
        label: {
          show: true,
          position: 'inside' as const,
          rotate: 'radial' as const,
          padding: 2,
          align: 'right' as const,
          silent: true,
          fontSize: manyOuter ? 10 : 11,
          // Skip labels on thin wedges — long names won't fit the arc.
          minAngle: 8,
          formatter: '{b}',
        },
      },
    ],
    data,
  };

  return {
    ...base,
    title: chartTitle(config.title, textColor, 'center'),
    // Names are on-wedge; tooltips on thin wedges cause enter/leave flicker.
    tooltip: { show: false },
    series: [series],
  };
}
